import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { publicApi } from '../lib/api';
import { Loader2, Monitor, Smartphone, X, Eye } from 'lucide-react';
import SapPageRenderer from '../components/SapPageRenderer';

// Preview route opened in a new tab from the SAP editor.
// - `?raw=1` renders ONLY the page (used inside the device iframe).
// - otherwise renders a toolbar (desktop/mobile toggle) + an iframe for a true responsive preview.
// Reads unsaved draft content from localStorage; never saves or publishes anything.
export default function SapPagePreview() {
  const [params] = useSearchParams();
  const raw = params.get('raw') === '1';
  const [device, setDevice] = useState('desktop');
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(raw);

  useEffect(() => {
    if (!raw) return; // wrapper does not need content itself
    const draft = localStorage.getItem('sap_preview_content');
    if (draft) {
      try { setContent(JSON.parse(draft)); setLoading(false); return; } catch (e) { /* fall back */ }
    }
    (async () => {
      try {
        const res = await publicApi.getSAPPage();
        setContent(res.data);
      } catch (e) {
        setContent(null);
      } finally {
        setLoading(false);
      }
    })();
  }, [raw]);

  // Raw mode: just render the page (inside the iframe)
  if (raw) {
    if (loading) {
      return <div className="min-h-screen bg-[#F9F6F0] flex items-center justify-center"><Loader2 className="w-12 h-12 animate-spin text-[#A5BBA8]" /></div>;
    }
    if (!content) {
      return <div className="min-h-screen bg-[#F9F6F0] flex items-center justify-center text-[#5C6A61]">Aperçu indisponible.</div>;
    }
    return <SapPageRenderer content={content} />;
  }

  // Wrapper mode: toolbar + responsive iframe
  return (
    <div className="min-h-screen bg-[#5C6A61]/20 flex flex-col" data-testid="sap-preview-page">
      <div className="flex items-center justify-between gap-4 px-4 py-3 bg-[#2A312C] text-white flex-shrink-0">
        <div className="flex items-center gap-2 min-w-0">
          <Eye className="w-5 h-5 flex-shrink-0" />
          <span className="font-medium">Aperçu avant publication</span>
          <span className="text-xs bg-white/15 px-2 py-1 rounded ml-2 hidden sm:inline">Modifications en cours · non enregistré</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center bg-white/10 rounded-lg p-1">
            <button onClick={() => setDevice('desktop')}
              className={`px-3 py-1.5 rounded-md text-sm inline-flex items-center gap-1 ${device === 'desktop' ? 'bg-white text-[#2A312C]' : 'text-white'}`}
              data-testid="preview-desktop-btn">
              <Monitor className="w-4 h-4" />Ordinateur
            </button>
            <button onClick={() => setDevice('mobile')}
              className={`px-3 py-1.5 rounded-md text-sm inline-flex items-center gap-1 ${device === 'mobile' ? 'bg-white text-[#2A312C]' : 'text-white'}`}
              data-testid="preview-mobile-btn">
              <Smartphone className="w-4 h-4" />Mobile
            </button>
          </div>
          <button onClick={() => window.close()} className="px-3 py-1.5 rounded-lg bg-white text-[#2A312C] text-sm inline-flex items-center gap-1" data-testid="preview-close-btn">
            <X className="w-4 h-4" />Fermer
          </button>
        </div>
      </div>
      <div className="flex-1 overflow-auto flex justify-center py-6">
        <div className="bg-white shadow-2xl transition-all duration-300 overflow-hidden"
          style={device === 'mobile'
            ? { width: 390, height: '100%', borderRadius: 24 }
            : { width: '100%', maxWidth: 1280, height: '100%', borderRadius: 8 }}>
          <iframe title="Aperçu page SAP" src="/sap-preview?raw=1" className="w-full h-full border-0" data-testid="sap-preview-iframe" />
        </div>
      </div>
    </div>
  );
}
